/**
 * EmailJS configuration (Release 2.0.0, task T06).
 *
 * The contact form previously fell back to committed literals when the
 * `REACT_APP_EMAILJS_*` variables were unset, so a misconfigured build shipped
 * working credentials from source instead of failing. There is no fallback any
 * more: the three variables are the only source, and a build without them
 * produces a form that says so rather than one that silently misbehaves.
 *
 * Create React App substitutes `process.env.REACT_APP_*` at build time only for
 * full static member expressions — `process.env[name]` is never replaced — so
 * each variable is read literally once, here.
 */

export const EMAILJS_ENV_VARS = Object.freeze([
  'REACT_APP_EMAILJS_SERVICE_ID',
  'REACT_APP_EMAILJS_TEMPLATE_ID',
  'REACT_APP_EMAILJS_PUBLIC_KEY',
]);

const BUILD_ENV = Object.freeze({
  REACT_APP_EMAILJS_SERVICE_ID: process.env.REACT_APP_EMAILJS_SERVICE_ID,
  REACT_APP_EMAILJS_TEMPLATE_ID: process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
  REACT_APP_EMAILJS_PUBLIC_KEY: process.env.REACT_APP_EMAILJS_PUBLIC_KEY,
});

/** The variable names that are absent or blank, in declaration order. */
export function missingEmailJsVars(env = BUILD_ENV) {
  return EMAILJS_ENV_VARS.filter((name) => {
    const value = env[name];
    return typeof value !== 'string' || value.trim() === '';
  });
}

export function isEmailJsConfigured(env = BUILD_ENV) {
  return missingEmailJsVars(env).length === 0;
}

export function emailJsConfigError(missing) {
  return (
    'EmailJS is not configured: ' +
    missing.join(', ') +
    ' must be set at build time. See frontend/.env.example.'
  );
}

/**
 * Returns the configuration or throws a message naming every missing variable.
 * The contact form calls this instead of sending with undefined credentials.
 */
export function requireEmailJsConfig(env = BUILD_ENV) {
  const missing = missingEmailJsVars(env);
  if (missing.length > 0) throw new Error(emailJsConfigError(missing));
  return {
    serviceId: env.REACT_APP_EMAILJS_SERVICE_ID,
    templateId: env.REACT_APP_EMAILJS_TEMPLATE_ID,
    publicKey: env.REACT_APP_EMAILJS_PUBLIC_KEY,
  };
}
